import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "420",
  "slug": "spot-a-volcano-from-satellite-imagery",
  "title": "How to Spot a Volcano From Space",
  "excerpt": "Cones, craters, calderas, and dark lava flows give volcanoes away from above. Here is how to recognise a volcano in satellite imagery and where to expect them.",
  "date": "2026-06-12",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1623202689412-31aba47a3ead?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx2b2xjYW5vJTIwY3JhdGVyJTIwYWVyaWFsfGVufDF8MHx8fDE3ODEyNTIzMDZ8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "Volcanoes give themselves away from above through circular shapes: a symmetrical cone with a summit crater, or a large round caldera, often holding a lake. Look also for radial drainage spreading from the peak, dark fresh lava flows, and a location along a plate boundary or hotspot.",
  "content": [
    {
      "type": "paragraph",
      "text": "Volcanoes are some of the most satisfying features to find in satellite imagery, because they tend to announce themselves. Where a coastline or a city can be ambiguous, a volcano usually has a shape and a set of details that are hard to mistake for anything else. Once you learn the signs, you will start spotting them all over the map."
    },
    {
      "type": "heading",
      "text": "The classic cone and crater"
    },
    {
      "type": "paragraph",
      "text": "The most recognisable volcano is the stratovolcano, the steep, symmetrical cone that matches the picture in everyone's head, like Japan's Mount Fuji or the Philippines' Mayon. From directly above, a cone shows up as a near-perfect circle, often with a small dark crater dimple at the centre and slopes radiating evenly outward. A tidy round mountain with a pit at the top is almost always volcanic."
    },
    {
      "type": "paragraph",
      "text": "Not all volcanoes are tall and steep, though. Shield volcanoes, like those in Hawaii, are broad and gently sloped, built from runny lava that spreads far before it cools. They cover a lot of ground without forming a sharp peak, so they read as wide, low domes rather than dramatic cones."
    },
    {
      "type": "heading",
      "text": "Calderas and crater lakes"
    },
    {
      "type": "paragraph",
      "text": "When a volcano empties its magma chamber and the summit collapses, it leaves a caldera, a large circular basin that can be many kilometres across. Calderas are some of the clearest volcanic signatures from space, especially when they fill with water to form a round crater lake, like Crater Lake in Oregon or the flooded ring of Santorini. A suspiciously circular lake ringed by steep walls is a strong sign of a collapsed volcano."
    },
    {
      "type": "heading",
      "text": "Radial drainage and reading age from colour"
    },
    {
      "type": "paragraph",
      "text": "Two more clues confirm a volcano even when the shape is worn down. The first is radial drainage: streams and gullies running outward from the summit in every direction, like spokes on a wheel, because water flows downhill from a central high point. The second is colour, which hints at age. Fresh lava flows are dark, often nearly black, with little vegetation, and they spread in long tongues and fans down the slopes. As a flow ages, plants slowly colonise it and it greens over, so a volcano can show a patchwork of dark recent flows over older vegetated ones."
    },
    {
      "type": "heading",
      "text": "Volcanic fields and other tells"
    },
    {
      "type": "paragraph",
      "text": "Not every volcano is a single mountain. Some regions are dotted with volcanic fields, clusters of small cinder cones that look like a scattering of little circular bumps, each with its own tiny crater. Other giveaways include a snow-capped peak standing alone above lower country, even in the tropics, and the occasional steam plume drifting from a summit. Around active vents the ground can take on yellowish, altered tones from sulphur and heat."
    },
    {
      "type": "heading",
      "text": "Where to expect them"
    },
    {
      "type": "paragraph",
      "text": "Volcanoes are not scattered randomly. They cluster where the Earth's plates meet or where deep hotspots punch through the crust, so location is a clue in itself:"
    },
    {
      "type": "list",
      "items": [
        "The Ring of Fire, the horseshoe of plate boundaries around the Pacific, holds most of the world's active volcanoes",
        "The East African Rift, where the continent is splitting apart, is lined with them",
        "Hotspots like Hawaii and Iceland build volcanoes far from any plate edge",
        "Long mountain chains above subduction zones, like the Andes, are studded with volcanic peaks"
      ]
    }
  ]
};
